import { PAGINATION_PAGES_BUFFER, PAGINATION_PAGES_LIMIT, SEPARATOR } from '../pagination-config';

import type { PageNavigationItemsListType } from '../pagination/pagination.types';

/** Number of items shown before ellipsis (limit minus buffer minus 1 for the boundary page) */
const ITEMS_BEFORE_ELLIPSIS = PAGINATION_PAGES_LIMIT - PAGINATION_PAGES_BUFFER - 1;

/** Threshold for determining if current page is near the start (triggers right-side truncation only) */
const LEFT_THRESHOLD = PAGINATION_PAGES_LIMIT - PAGINATION_PAGES_BUFFER * 2;

/** Number of pages in the middle section when both sides are truncated */
const MIDDLE_SECTION_SIZE = PAGINATION_PAGES_LIMIT - PAGINATION_PAGES_BUFFER * 2;

/**
 * Generates an array of consecutive numbers starting from a given value
 * @param from - The starting number
 * @param count - How many numbers to generate
 * @returns Array of numbers from `from` to `from + count - 1`
 * @example range(5, 3) => [5, 6, 7]
 */
export const range = (from: number, count: number): number[] => {
  return Array.from({ length: count }, (_, index) => index + from);
};

/**
 * This function generates the pagination navigation links and separators.
 * The output is an array of numbers and separators
 * Eg., [1, 2, 3, 4, 5, 6, 7, '...', 10]
 * @param currentPageIndex index of the current page
 * @param totalPages total number of pages
 */
export const getNavItems = (currentPageIndex: number, totalPages: number): PageNavigationItemsListType => {
  const navItems: PageNavigationItemsListType = [];
  const lastPageIndex = totalPages - 1;

  if (totalPages <= PAGINATION_PAGES_LIMIT) {
    // If it is under the pages limit, just show all the pages
    navItems.push(...range(0, totalPages));
  } else {
    // If not, start by adding the first page, since the first and last will always be shown
    navItems.push(0);

    // On the left, we want to show one extra page after the selected page
    // Check if the current page index is under that threshold
    // If the page limit is 9, the currentPageIndex should be below 5 (page no 6), so we have space
    // to show pages 7, '...', <last page>, totalling the number of shown items to 9
    if (currentPageIndex <= LEFT_THRESHOLD) {
      navItems.push(...range(1, ITEMS_BEFORE_ELLIPSIS));
    } else {
      navItems.push(SEPARATOR);
    }

    // If the current page is not close to the start or the end pages, then show the current page
    // and pages equal to the buffer on both sides
    if (currentPageIndex > LEFT_THRESHOLD && currentPageIndex < lastPageIndex - PAGINATION_PAGES_BUFFER - 1) {
      navItems.push(...range(currentPageIndex - PAGINATION_PAGES_BUFFER, MIDDLE_SECTION_SIZE));
    }

    // On the right too, we want to show one extra page before the selected page
    if (currentPageIndex >= lastPageIndex - PAGINATION_PAGES_BUFFER - 1) {
      navItems.push(...range(totalPages - (PAGINATION_PAGES_LIMIT - PAGINATION_PAGES_BUFFER), ITEMS_BEFORE_ELLIPSIS));
    } else {
      navItems.push(SEPARATOR);
    }

    // End by adding the last page, since the first and last will always be shown
    navItems.push(lastPageIndex);
  }

  return navItems;
};
